import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { BookOpen, Clock, Users, Search, Play, ArrowRight } from 'lucide-react';
import { Subject, Course } from '@/types/course';

interface CoursesDashboardProps {
  onSelectCourse: (course: Course) => void;
}

export default function CoursesDashboard({ onSelectCourse }: CoursesDashboardProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClass, setSelectedClass] = useState('JSS1');

  const subjects: Subject[] = [
    { id: '1', name: 'Mathematics', code: 'MATH', icon: '📐', courseCount: 4, classes: ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'] },
    { id: '2', name: 'English', code: 'ENG', icon: '📚', courseCount: 4, classes: ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'] },
    { id: '3', name: 'Science', code: 'SCI', icon: '🔬', courseCount: 3, classes: ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'] },
    { id: '4', name: 'History', code: 'HIS', icon: '📖', courseCount: 3, classes: ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'] },
    { id: '5', name: 'Geography', code: 'GEO', icon: '🗺️', courseCount: 3, classes: ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'] },
    { id: '6', name: 'Civic Education', code: 'CIV', icon: '🏛️', courseCount: 3, classes: ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'] },
  ];

  const enrolledCourses: Course[] = [
    {
      id: '1',
      name: 'Mathematics Fundamentals',
      subject: 'Mathematics',
      class: 'JSS1',
      description: 'Learn the basics of algebra, geometry, and arithmetic',
      duration: 24,
      lectureCount: 12,
      enrolledStudents: 120,
      lectures: [],
      status: 'published',
      createdAt: new Date(),
    },
    {
      id: '2',
      name: 'English Grammar Mastery',
      subject: 'English',
      class: 'JSS1',
      description: 'Complete guide to English grammar and composition',
      duration: 20,
      lectureCount: 10,
      enrolledStudents: 115,
      lectures: [],
      status: 'published',
      createdAt: new Date(),
    },
    {
      id: '3',
      name: 'Physics for Beginners',
      subject: 'Science',
      class: 'JSS1',
      description: 'Introduction to basic physics concepts and experiments',
      duration: 18,
      lectureCount: 9,
      enrolledStudents: 100,
      lectures: [],
      status: 'published',
      createdAt: new Date(),
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const classes = ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-4">
        <div>
          <h1 className="text-3xl font-bold text-primary">Learning Courses</h1>
          <p className="text-muted-foreground">Master every subject with our comprehensive video lectures</p>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search courses, subjects, or topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 border-border"
          />
        </div>
      </div>

      {/* Class Filter */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-foreground">Filter by Class</label>
        <div className="flex flex-wrap gap-2">
          {classes.map((cls) => (
            <Button
              key={cls}
              onClick={() => setSelectedClass(cls)}
              variant={selectedClass === cls ? 'default' : 'outline'}
              className={`${
                selectedClass === cls
                  ? 'bg-primary hover:bg-primary/90'
                  : 'border-border hover:border-primary'
              }`}
              size="sm"
            >
              {cls}
            </Button>
          ))}
        </div>
      </div>

      {/* Subjects Grid */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-primary">Subjects</h2>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {subjects.map((subject) => (
            <motion.div key={subject.id} variants={itemVariants}>
              <Card className="p-6 border-border hover:shadow-lg hover:border-secondary transition-all duration-300 cursor-pointer group">
                <div className="flex items-start justify-between mb-4">
                  <div className="text-4xl">{subject.icon}</div>
                  <Badge variant="outline" className="bg-secondary/10 text-secondary">
                    {subject.courseCount} courses
                  </Badge>
                </div>
                <h3 className="text-xl font-semibold text-primary mb-2">{subject.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Available for all classes
                </p>
                <Button
                  variant="outline"
                  className="w-full border-secondary text-secondary hover:bg-secondary/5 gap-2 group-hover:border-primary"
                >
                  Explore <ArrowRight className="w-4 h-4" />
                </Button>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Enrolled Courses */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-primary">Your Courses</h2>
          <Badge variant="outline" className="bg-accent/10">
            {enrolledCourses.length} Enrolled
          </Badge>
        </div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="space-y-4"
        >
          {enrolledCourses.map((course, index) => (
            <motion.div key={course.id} variants={itemVariants}>
              <Card className="overflow-hidden border-border hover:border-secondary hover:shadow-lg transition-all duration-300">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-6">
                  {/* Thumbnail */}
                  <div className="md:col-span-1">
                    <div className="relative w-full aspect-video bg-gradient-to-br from-primary/20 to-secondary/20 rounded-lg overflow-hidden group cursor-pointer"
                      onClick={() => onSelectCourse(course)}>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/30 transition-colors">
                        <Play className="w-12 h-12 text-white opacity-80 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </div>

                  {/* Course Info */}
                  <div className="md:col-span-3 flex flex-col justify-between">
                    <div className="space-y-2 mb-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-primary">{course.name}</h3>
                          <p className="text-sm text-muted-foreground">{course.subject}</p>
                        </div>
                        <Badge className="bg-secondary/10 text-secondary">{course.class}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{course.description}</p>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-4 pb-4 border-b border-border">
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-secondary" />
                        <span className="text-muted-foreground">{course.duration}h</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <BookOpen className="w-4 h-4 text-secondary" />
                        <span className="text-muted-foreground">{course.lectureCount} lectures</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="w-4 h-4 text-secondary" />
                        <span className="text-muted-foreground">{course.enrolledStudents} students</span>
                      </div>
                    </div>

                    {/* Progress */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="font-semibold text-primary">{25 + index * 15}%</span>
                      </div>
                      <Progress value={25 + index * 15} className="h-2" />
                    </div>

                    {/* Button */}
                    <Button
                      onClick={() => onSelectCourse(course)}
                      className="mt-4 w-full bg-primary hover:bg-primary/90 gap-2"
                    >
                      Continue Learning <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
